/**
 * useProfile Hook
 * Manages veteran profile data, service history, and claims
 */

import { useState, useEffect, useCallback } from 'react';
import profileService from '../services/profileService';

export const useProfile = (userId) => {
  const [profileData, setProfileData] = useState(null);
  const [serviceHistory, setServiceHistory] = useState({ services: [], serviceEvents: [] });
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load profile data
  const loadProfileData = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load all profile data in parallel
      const [profile, history, userClaims] = await Promise.all([
        profileService.getProfile(userId),
        profileService.getServiceHistory(userId),
        profileService.getClaims(userId)
      ]);

      setProfileData(profile);
      setServiceHistory(history || { services: [], serviceEvents: [] });
      setClaims(userClaims || []);

    } catch (err) {
      console.error('❌ Error loading profile data:', err);
      setError(err.message || 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Refresh profile data
  const refreshProfile = useCallback(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Update profile data
  const updateProfile = useCallback(async (updates) => {
    if (!userId) return;

    try {
      setLoading(true);
      console.log('Updating profile');

      const updatedProfile = await profileService.updateProfile(userId, updates);
      setProfileData(updatedProfile);

      console.log('✅ Profile updated successfully');
      return updatedProfile;

    } catch (err) {
      console.error('❌ Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Add service record
  const addServiceRecord = useCallback(async (serviceRecord) => {
    if (!userId) return;

    try {
      console.log('Adding service record');

      const newRecord = await profileService.addServiceRecord(userId, serviceRecord);
      setServiceHistory(prev => ({
        ...prev,
        services: [...(prev.services || []), newRecord]
      }));

      console.log('✅ Service record added successfully');
      return newRecord;

    } catch (err) {
      console.error('❌ Error adding service record:', err);
      setError(err.message || 'Failed to add service record');
      throw err;
    }
  }, [userId]);

  // Update service history (services and events)
  const updateServiceHistory = useCallback(async (serviceHistoryData) => {
    if (!userId) return;

    try {
      console.log('Updating service history');

      const updatedHistory = await profileService.updateServiceHistory(userId, serviceHistoryData);
      setServiceHistory(updatedHistory);

      console.log('✅ Service history updated successfully');
      return updatedHistory;

    } catch (err) {
      console.error('❌ Error updating service history:', err);
      setError(err.message || 'Failed to update service history');
      throw err;
    }
  }, [userId]);

  // Add claim
  const addClaim = useCallback(async (claimData) => {
    if (!userId) return;

    try {
      console.log('Adding claim');

      const newClaim = await profileService.addClaim(userId, claimData);
      setClaims(prev => [...prev, newClaim]);

      console.log('✅ Claim added successfully');
      return newClaim;

    } catch (err) {
      console.error('❌ Error adding claim:', err);
      setError(err.message || 'Failed to add claim');
      throw err;
    }
  }, [userId]);

  // Update claim
  const updateClaim = useCallback(async (claimId, updates) => {
    if (!userId) return;

    try {
      console.log('Updating claim');

      const updatedClaim = await profileService.updateClaim(userId, claimId, updates);
      setClaims(prev => prev.map(claim =>
        claim.claimId === claimId ? updatedClaim : claim
      ));

      console.log('✅ Claim updated successfully');
      return updatedClaim;

    } catch (err) {
      console.error('❌ Error updating claim:', err);
      setError(err.message || 'Failed to update claim');
      throw err;
    }
  }, [userId]);

  // Load data on mount and when userId changes
  useEffect(() => {
    loadProfileData();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Data
    profileData,
    serviceHistory,
    claims,

    // State
    loading,
    error,

    // Actions
    refreshProfile,
    updateProfile,
    addServiceRecord,
    updateServiceHistory,
    addClaim,
    updateClaim,

    // Utils
    clearError: () => setError(null)
  };
};
